import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  KeyboardEvent,
} from 'react';
import {
 Send, Mic, XIcon
} from 'lucide-react';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
 BsRecordCircle
} from 'react-icons/bs';
import RecordRTC, {
 StereoAudioRecorder
} from 'recordrtc';
import {
 Context
} from 'shared/context/GlobalState';

interface VoiceMessagePayload {
  type: 'voice';
  audioBlob: Blob;
  duration: number;
}

interface WebchatFooterProps {
  loading: boolean;
  emitMessage: (msg: string | VoiceMessagePayload) => void;
  socket: WebSocket | null;
  convoHistory: unknown;
  primaryColor: string;
  textPrimaryColor: string;
  accentColor?: string;
}

const WebchatFooter: React.FC<WebchatFooterProps> = ({
  loading,
  emitMessage,
  socket,
  convoHistory,
  primaryColor,
  textPrimaryColor,
  accentColor = 'black',
}) => {
  const [userMessage, setUserMessage] = useState<string>('');
  const [recording, setRecording] = useState<boolean>(false);
  const [recordingTime, setRecordingTime] = useState<number>(0);

  const recorder = useRef<RecordRTC | null>(null);

  const {
 addNewNotifcation
} = useContext(Context);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (recording) {
      timer = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } else if (timer) {
      clearInterval(timer);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [recording]);

  const handleSendMessage = () => {
    if (loading) {
      loadingWarning();
      return;
    }

    if (userMessage.trim()) {
      emitMessage(userMessage);
      setUserMessage('');
    }
  };

  const handleVoiceRecording = async () => {
    startRecording();
  };

  const handleSendVoice = async () => {
    stopRecording();
  };

  const handleCancelRecording = async () => {
    setRecording(false);
    if (recorder.current) {
      recorder.current.stopRecording(() => {
        recorder.current?.destroy();
        recorder.current = null;
      });
    }
    setRecordingTime(0);
  };

  const startRecording = async () => {
    if (loading) {
      loadingWarning();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const newRecorder = new RecordRTC(stream, {
        type: 'audio',
        mimeType: 'audio/wav',
        recorderType: StereoAudioRecorder,
        desiredSampRate: 16000,
        numberOfAudioChannels: 1,
      });

      newRecorder.startRecording();
      recorder.current = newRecorder;
      setRecording(true);
      setRecordingTime(0);
    } catch (error) {
      addNewNotifcation(
        'Could not access your microphone. Please check permissions.',
        'warning'
      );
    }
  };

  const stopRecording = async () => {
    setRecording(false);
    if (recorder.current) {
      recorder.current.stopRecording(() => {
        const blob = recorder.current!.getBlob();
        emitMessage({
          type: 'voice',
          audioBlob: blob,
          duration: recordingTime,
        });

        recorder.current?.destroy();
        recorder.current = null;
      });
    }
  };

  const loadingWarning = () => {
    addNewNotifcation('Please wait untill the response comes.', 'warning');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes > 0 ? `${minutes}m ` : ''}${seconds}s`;
  };

  return (
    <div className="p-3 border-t" style={{ backgroundColor: primaryColor }} data-no-drag>
      {!recording ? (
        <div className="flex items-center space-x-2">
          <Textarea
            onChange={(e) => setUserMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            value={userMessage}
            rows={1}
            placeholder="Type Something....."
            className="flex-grow bg-transparent min-h-1 text-gray-800 placeholder-gray-400 border-gray-300 resize-none"
            style={{ backgroundColor: primaryColor, color: textPrimaryColor }}
          />
          <div className="flex items-center gap-2" style={{ color: accentColor }}>
            <div
              className="cursor-pointer"
              onClick={handleVoiceRecording}
              data-no-drag
            >
              <Mic className="h-6 w-6 hover:text-[#8c8c8c]" />
            </div>
            <Send
              className="h-5 w-5 cursor-pointer hover:text-[#8c8c8c]"
              onClick={handleSendMessage}
            />
          </div>
        </div>
      ) : (
        <div className="flex items-center w-full gap-3">
          <div
            className="cursor-pointer"
            onClick={handleCancelRecording}
            data-no-drag
          >
            <XIcon size={24} className="text-[#b1092a] hover:text-[#8c1a2a]" />
          </div>

          <div className="flex-1 flex items-center gap-3 px-3 py-2 rounded-lg border"
            style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca' }}
          >
            <BsRecordCircle
              size={16}
              className="text-red-500 animate-pulse flex-shrink-0"
            />

            <div className="flex-1 flex items-center gap-1" style={{ height: '32px' }}>
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-full animate-pulse"
                  style={{
                    width: '3px',
                    height: `${Math.random() * 20 + 8}px`,
                    animationDelay: `${i * 0.1}s`,
                    animationDuration: '1s',
                    backgroundColor: '#f87171',
                  }}
                />
              ))}
            </div>

            <span className="text-sm font-medium flex-shrink-0" style={{ color: '#dc2626' }}>
              {formatTime(recordingTime)}
            </span>
          </div>

          <div
            className="cursor-pointer"
            onClick={handleSendVoice}
            data-no-drag
          >
            <div
              className="p-2 rounded-full"
              style={{
                backgroundColor: accentColor,
              }}
            >
              <Send className="h-5 w-5 text-white" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebchatFooter;
